import { Box, Heading, HStack, Separator, Spinner, Text } from "@chakra-ui/react-v3";
import { useMemo } from "react";
import Markdown from "react-markdown";
import {
  useAnalysisStateContext,
  useDocumentCallbackContext,
  useFhirSdkContext,
} from "../side-panels/sidepanel-context";
import { createCustomRenderer } from "../utils/markdown-renderer";

/**
 * Component that displays AI analysis for both series and single resources.
 * This is a display-only component that receives pre-fetched analysis state.
 */
export function ResourceAnalysis() {
  const analysisState = useAnalysisStateContext();
  const fhirSdk = useFhirSdkContext();
  const { onPdfView } = useDocumentCallbackContext();

  // Create context-aware renderer with bundle access
  const customRenderer = useMemo(() => {
    return createCustomRenderer(fhirSdk, onPdfView);
  }, [fhirSdk, onPdfView]);

  if (!analysisState) return null;

  const isLoading = analysisState.loading;
  const error = analysisState.error;
  const analysis = analysisState.analysis;

  return (
    <>
      <Box spaceY={4}>
        <HStack mb={4}>
          <Heading size="md" color="fg.emphasized">
            AI Analysis
          </Heading>
          {isLoading && (
            <HStack>
              <Spinner size="sm" color="status.info" />
              <Text textStyle="body.sm" color="status.info">
                Analyzing...
              </Text>
            </HStack>
          )}
        </HStack>

        {isLoading && (
          <Text color="fg.subtle" textStyle="body.sm" fontStyle="italic">
            Analyzing patient record...
          </Text>
        )}

        {error && (
          <Text color="fg.subtle" textStyle="body.sm" fontStyle="italic">
            No analysis found.
          </Text>
        )}

        {analysis && (
          <Box
            p={3}
            bg="bg.panel"
            borderRadius="md"
            borderLeft="3px solid"
            borderColor="status.info"
          >
            <Markdown components={customRenderer}>{analysis}</Markdown>
          </Box>
        )}
      </Box>
      <Separator borderColor="border" />
    </>
  );
}
